<script>
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import {
  required,
  requiredIf,
  minLength,
  maxLength,
} from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import { isValidPassword } from 'shared/helpers/Validators';
import NextButton from 'dashboard/components-next/button/Button.vue';

// Devise: config.password_length = 6..128 (config/initializers/devise.rb)
const MIN_PASSWORD_LENGTH = 6;
const MAX_PASSWORD_LENGTH = 128;

export default {
  components: {
    NextButton,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      currentPassword: '',
      password: '',
      passwordConfirmation: '',
      // Revelacao por campo: mostrar um nao deve expor os outros.
      revealed: {
        currentPassword: false,
        password: false,
        passwordConfirmation: false,
      },
      isPasswordChanging: false,
      errorMessage: '',
      inputStyles: {
        borderRadius: '0.75rem',
        padding: '0.375rem 0.75rem',
        fontSize: '0.875rem',
        marginBottom: '0.125rem',
      },
    };
  },
  validations: {
    // So exigida de quem ja definiu a propria senha: quem nunca definiu nao
    // tem como saber a aleatoria gerada no provisionamento.
    currentPassword: {
      required: requiredIf(function requiresCurrentPassword() {
        return this.hasOwnPassword;
      }),
    },
    // Espelha as regras do backend: devise + password_has_required_content
    // (1 maiuscula, 1 minuscula, 1 numero e 1 caractere especial).
    password: {
      required,
      minLength: minLength(MIN_PASSWORD_LENGTH),
      maxLength: maxLength(MAX_PASSWORD_LENGTH),
      isValidPassword,
    },
    passwordConfirmation: {
      required,
      isEqPassword(value) {
        if (value !== this.password) {
          return false;
        }
        return true;
      },
    },
  },
  computed: {
    ...mapGetters({ currentUser: 'getCurrentUser' }),
    // O backend marca `password_set_by_user` na primeira definicao e devolve o
    // usuario atualizado, entao o formulario troca de modo sozinho depois dela.
    hasOwnPassword() {
      return Boolean(this.currentUser?.custom_attributes?.password_set_by_user);
    },
    submitLabel() {
      return this.hasOwnPassword
        ? this.$t('PROFILE_SETTINGS.FORM.PASSWORD_SECTION.BTN_TEXT')
        : this.$t('PROFILE_SETTINGS.FORM.PASSWORD_SECTION.BTN_TEXT_FIRST');
    },
    sectionNote() {
      return this.hasOwnPassword
        ? this.$t('PROFILE_SETTINGS.FORM.PASSWORD_SECTION.NOTE')
        : this.$t('PROFILE_SETTINGS.FORM.PASSWORD_SECTION.NOTE_FIRST');
    },
    isButtonDisabled() {
      return this.isPasswordChanging || this.v$.$invalid;
    },
    passwordError() {
      const field = this.v$.password;
      if (!field.$error) return '';
      if (field.required.$invalid) {
        return this.$t('PROFILE_SETTINGS.FORM.PASSWORD.REQUIRED_ERROR');
      }
      if (field.minLength.$invalid || field.maxLength.$invalid) {
        return this.$t('PROFILE_SETTINGS.FORM.PASSWORD.ERROR');
      }
      return this.$t('PROFILE_SETTINGS.FORM.PASSWORD.COMPLEXITY_ERROR');
    },
    passwordConfirmationError() {
      const field = this.v$.passwordConfirmation;
      if (!field.$error) return '';
      if (field.required.$invalid) {
        return this.$t(
          'PROFILE_SETTINGS.FORM.PASSWORD_CONFIRMATION.REQUIRED_ERROR'
        );
      }
      return this.$t('PROFILE_SETTINGS.FORM.PASSWORD_CONFIRMATION.ERROR');
    },
  },
  methods: {
    // O slot `masked` do woot-input cai depois do input, dentro do <label>, e o
    // rotulo empurra o campo para baixo: dai o `top-7` (o input comeca a 28px
    // do topo do label) e o `h-10` (a altura do proprio input). Ancorar pelo
    // topo, e nao pelo fundo, porque a mensagem de erro cresce por baixo.
    inputType(field) {
      return this.revealed[field] ? 'text' : 'password';
    },
    toggleLabel(field) {
      return this.revealed[field]
        ? this.$t('PROFILE_SETTINGS.FORM.PASSWORD_SECTION.HIDE_PASSWORD')
        : this.$t('PROFILE_SETTINGS.FORM.PASSWORD_SECTION.SHOW_PASSWORD');
    },
    toggleReveal(field) {
      this.revealed[field] = !this.revealed[field];
    },
    resetForm() {
      this.currentPassword = '';
      this.password = '';
      this.passwordConfirmation = '';
      // Nao deixar um campo revelado apos limpar: o proximo valor digitado
      // apareceria em claro sem o usuario ter pedido.
      this.revealed.currentPassword = false;
      this.revealed.password = false;
      this.revealed.passwordConfirmation = false;
      this.v$.$reset();
    },
    async changePassword() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        useAlert(this.$t('PROFILE_SETTINGS.FORM.ERROR'));
        return;
      }
      this.isPasswordChanging = true;
      // Lido antes do dispatch: a resposta marca `password_set_by_user` e o
      // `hasOwnPassword` vira true no meio do caminho.
      let alertMessage = this.hasOwnPassword
        ? this.$t('PROFILE_SETTINGS.PASSWORD_UPDATE_SUCCESS')
        : this.$t('PROFILE_SETTINGS.PASSWORD_DEFINE_SUCCESS');
      try {
        await this.$store.dispatch('updatePassword', {
          password: this.password,
          passwordConfirmation: this.passwordConfirmation,
          currentPassword: this.currentPassword,
        });
        // Sem isto os campos seguem preenchidos com a senha ja trocada e o
        // proximo envio volta com "senha atual invalida".
        this.resetForm();
      } catch (error) {
        alertMessage =
          parseAPIErrorResponse(error) ||
          this.$t('RESET_PASSWORD.API.ERROR_MESSAGE');
      } finally {
        this.isPasswordChanging = false;
        useAlert(alertMessage);
      }
    },
  },
};
</script>

<template>
  <form @submit.prevent="changePassword()">
    <div class="flex flex-col w-full gap-4">
      <p class="mt-0 mb-0 text-sm text-n-slate-11">{{ sectionNote }}</p>

      <woot-input
        v-if="hasOwnPassword"
        v-model="currentPassword"
        :type="inputType('currentPassword')"
        :styles="inputStyles"
        class="relative ltr:[&>input]:!pr-10 rtl:[&>input]:!pl-10"
        :class="{ error: v$.currentPassword.$error }"
        :label="$t('PROFILE_SETTINGS.FORM.CURRENT_PASSWORD.LABEL')"
        :placeholder="$t('PROFILE_SETTINGS.FORM.CURRENT_PASSWORD.PLACEHOLDER')"
        :error="`${
          v$.currentPassword.$error
            ? $t('PROFILE_SETTINGS.FORM.CURRENT_PASSWORD.ERROR')
            : ''
        }`"
        @input="v$.currentPassword.$touch"
        @blur="v$.currentPassword.$touch"
      >
        <template #masked>
          <button
            type="button"
            class="absolute inline-flex items-center justify-center w-8 h-10 top-7 ltr:right-1 rtl:left-1 text-n-slate-11 hover:text-n-slate-12"
            :aria-label="toggleLabel('currentPassword')"
            :aria-pressed="revealed.currentPassword"
            tabindex="-1"
            @click="toggleReveal('currentPassword')"
          >
            <span
              class="size-4"
              :class="
                revealed.currentPassword ? 'i-lucide-eye-off' : 'i-lucide-eye'
              "
            />
          </button>
        </template>
      </woot-input>

      <div>
        <woot-input
          v-model="password"
          :type="inputType('password')"
          :styles="inputStyles"
          class="relative ltr:[&>input]:!pr-10 rtl:[&>input]:!pl-10"
          :class="{ error: v$.password.$error }"
          :label="$t('PROFILE_SETTINGS.FORM.PASSWORD.LABEL')"
          :placeholder="$t('PROFILE_SETTINGS.FORM.PASSWORD.PLACEHOLDER')"
          :error="passwordError"
          @input="v$.password.$touch"
          @blur="v$.password.$touch"
        >
          <template #masked>
            <button
              type="button"
              class="absolute inline-flex items-center justify-center w-8 h-10 top-7 ltr:right-1 rtl:left-1 text-n-slate-11 hover:text-n-slate-12"
              :aria-label="toggleLabel('password')"
              :aria-pressed="revealed.password"
              tabindex="-1"
              @click="toggleReveal('password')"
            >
              <span
                class="size-4"
                :class="revealed.password ? 'i-lucide-eye-off' : 'i-lucide-eye'"
              />
            </button>
          </template>
        </woot-input>
        <p class="mt-1 mb-0 text-xs text-n-slate-11">
          {{ $t('PROFILE_SETTINGS.FORM.PASSWORD.HINT') }}
        </p>
      </div>

      <woot-input
        v-model="passwordConfirmation"
        :type="inputType('passwordConfirmation')"
        :styles="inputStyles"
        class="relative ltr:[&>input]:!pr-10 rtl:[&>input]:!pl-10"
        :class="{ error: v$.passwordConfirmation.$error }"
        :label="$t('PROFILE_SETTINGS.FORM.PASSWORD_CONFIRMATION.LABEL')"
        :placeholder="
          $t('PROFILE_SETTINGS.FORM.PASSWORD_CONFIRMATION.PLACEHOLDER')
        "
        :error="passwordConfirmationError"
        @input="v$.passwordConfirmation.$touch"
        @blur="v$.passwordConfirmation.$touch"
      >
        <template #masked>
          <button
            type="button"
            class="absolute inline-flex items-center justify-center w-8 h-10 top-7 ltr:right-1 rtl:left-1 text-n-slate-11 hover:text-n-slate-12"
            :aria-label="toggleLabel('passwordConfirmation')"
            :aria-pressed="revealed.passwordConfirmation"
            tabindex="-1"
            @click="toggleReveal('passwordConfirmation')"
          >
            <span
              class="size-4"
              :class="
                revealed.passwordConfirmation
                  ? 'i-lucide-eye-off'
                  : 'i-lucide-eye'
              "
            />
          </button>
        </template>
      </woot-input>

      <div>
        <NextButton
          type="submit"
          :label="submitLabel"
          :is-loading="isPasswordChanging"
          :disabled="isButtonDisabled"
        />
      </div>
    </div>
  </form>
</template>
